import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar, Sparkles, ExternalLink, Disc3, Play, Loader2, Pause,
  Instagram, Twitter, Youtube, Globe, Music2,
} from "lucide-react";
import { api, mediumArt } from "@/lib/api";
import { usePlayer } from "@/contexts/PlayerContext";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "TBA";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

const typeColor = (type) => {
  const t = (type || "").toLowerCase();
  if (t === "album") return "bg-[#FF2E00]/20 text-[#FF99B3] border-[#FF2E00]/30";
  if (t === "ep") return "bg-[#FF2E00]/20 text-[#C99CFF] border-[#FF2E00]/30";
  if (t === "single") return "bg-[#E2FF31]/20 text-[#E2FF31] border-[#E2FF31]/30";
  if (t === "mixtape") return "bg-amber-400/20 text-amber-300 border-amber-400/30";
  return "bg-white/10 text-white/60 border-white/15";
};

const ComingSoonCard = ({ release }) => {
  const conf = Math.round((release.confidence || 0) * 100);
  const confColor =
    conf >= 80 ? "text-emerald-400" :
    conf >= 60 ? "text-[#E2FF31]" :
    conf >= 40 ? "text-amber-400" : "text-white/50";

  const { playTracks, current, playing, togglePlay } = usePlayer();
  const [resolving, setResolving] = useState(false);

  // A synthetic "track" we hand to PlayerContext. PlayerContext will call
  // /api/youtube/resolve under the hood to find a real YouTube videoId for
  // the upcoming song (snippet/teaser/audio leak). If nothing matches strict
  // title+artist validation, the resolver returns null and the player will
  // gracefully no-op (no previewUrl on these placeholder rows).
  const trackId = `upcoming:${release.artist_id}:${release.release_title}`;
  const isThis =
    current && (current.trackId === trackId || (current._isComingSoon && current._upcomingId === trackId));
  const isPlayingThis = isThis && playing;

  const handleListen = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    if (isPlayingThis) {
      togglePlay();
      return;
    }

    setResolving(true);
    try {
      // Probe the resolver first so we can tell the user clearly if nothing
      // is online for this release yet.
      const r = await api.get("/youtube/resolve", {
        params: { q: `${release.release_title} ${release.artist_name}`, title: release.release_title, artist: release.artist_name },
        timeout: 25000,
      });
      if (!r.data?.videoId) {
        toast.info("No preview available yet", {
          description: `${release.artist_name} hasn't released a snippet of "${release.release_title}" we can stream yet.`,
        });
        return;
      }
      playTracks([{
        trackId,
        trackName: release.release_title,
        artistName: release.artist_name,
        albumArtistName: release.artist_name,
        artworkUrl100: release.artwork_url,
        collectionName: "Upcoming release",
        previewUrl: null,                       // forces the YouTube engine path
        _youtubeId: r.data.videoId,             // hand-off video id (PlayerContext memoizes)
        _isComingSoon: true,
        _upcomingId: trackId,
      }], 0);
    } catch (err) {
      toast.error("Couldn't load preview");
    } finally {
      setResolving(false);
    }
  };

  return (
    <article
      data-testid={`coming-soon-card-${release.artist_id}-${release.release_title}`}
      className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/8 hover:border-white/20 transition-all p-4 flex gap-4"
    >
      <div className="shrink-0 relative">
        <Link
          to={release.artist_id?.match(/^\d+$/) ? `/artist/${release.artist_id}` : "#"}
          aria-label={`Open ${release.artist_name} page`}
        >
          {release.artwork_url ? (
            <img
              src={mediumArt(release.artwork_url)}
              alt=""
              className="w-20 h-20 rounded-xl object-cover shadow-lg group-hover:scale-105 transition-transform"
            />
          ) : (
            <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-[#FF2E00]/40 via-[#FF2E00]/30 to-[#E2FF31]/20 flex items-center justify-center">
              <Disc3 size={32} className="text-white/70" />
            </div>
          )}
        </Link>
        {/* Listen / Pause overlay */}
        <button
          type="button"
          onClick={handleListen}
          disabled={resolving}
          data-testid={`coming-soon-listen-${release.artist_id}-${release.release_title}`}
          className="absolute inset-0 m-auto w-9 h-9 rounded-full bg-black/65 backdrop-blur-sm flex items-center justify-center
                     opacity-0 group-hover:opacity-100 hover:scale-110 active:scale-95 transition disabled:opacity-100
                     border border-white/30"
          aria-label={isPlayingThis ? "Pause preview" : "Listen to preview"}
          title={isPlayingThis ? "Pause preview" : "Listen to preview"}
        >
          {resolving ? (
            <Loader2 size={15} className="text-white animate-spin" />
          ) : isPlayingThis ? (
            <Pause size={15} className="text-white" />
          ) : (
            <Play size={15} className="text-white fill-white ml-0.5" />
          )}
        </button>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <span className={`text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${typeColor(release.release_type)}`}>
            {release.release_type || "unknown"}
          </span>
          <span className={`text-[10px] font-semibold ${confColor} flex items-center gap-1`}>
            <Sparkles size={10} /> {conf}% AI confidence
          </span>
        </div>
        <h3 className="font-display text-lg font-semibold leading-tight truncate" title={release.release_title}>
          {release.release_title}
        </h3>
        <Link
          to={release.artist_id?.match(/^\d+$/) ? `/artist/${release.artist_id}` : "#"}
          className="text-sm text-white/60 hover:text-white hover:underline truncate block"
        >
          {release.artist_name}
        </Link>
        <div className="flex items-center gap-3 mt-2 text-xs text-white/50 flex-wrap">
          <span className="flex items-center gap-1">
            <Calendar size={12} />
            {fmtDate(release.expected_release_date)}
          </span>
          {release.source_urls?.[0] && (
            <a
              href={release.source_urls[0]}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:text-white"
            >
              <ExternalLink size={12} /> Source
            </a>
          )}
          {/* AI-detected social handles for the artist */}
          {release.socials && Object.keys(release.socials).length > 0 && (
            <span className="flex items-center gap-2" data-testid="coming-soon-socials">
              {release.socials.instagram && (
                <a href={release.socials.instagram} target="_blank" rel="noopener noreferrer" title="Instagram"
                   className="text-white/55 hover:text-[#E1306C]">
                  <Instagram size={12} />
                </a>
              )}
              {release.socials.twitter && (
                <a href={release.socials.twitter} target="_blank" rel="noopener noreferrer" title="X / Twitter"
                   className="text-white/55 hover:text-white">
                  <Twitter size={12} />
                </a>
              )}
              {release.socials.tiktok && (
                <a href={release.socials.tiktok} target="_blank" rel="noopener noreferrer" title="TikTok"
                   className="text-white/55 hover:text-[#69C9D0]">
                  <Music2 size={12} />
                </a>
              )}
              {release.socials.youtube && (
                <a href={release.socials.youtube} target="_blank" rel="noopener noreferrer" title="YouTube"
                   className="text-white/55 hover:text-[#FF0000]">
                  <Youtube size={12} />
                </a>
              )}
              {release.socials.homepage && (
                <a href={release.socials.homepage} target="_blank" rel="noopener noreferrer" title="Official site"
                   className="text-white/55 hover:text-white">
                  <Globe size={12} />
                </a>
              )}
            </span>
          )}
        </div>
        {release.notes && (
          <p className="text-xs text-white/40 mt-1.5 line-clamp-2 leading-relaxed">
            {release.notes}
          </p>
        )}
      </div>
    </article>
  );
};

export default ComingSoonCard;
